import { observer } from 'mobx-react';
import { fromEmpty, fromNullable } from 'maybeasy';
import { InitialReady, LicensesReady, FetchingLicenseOptions } from '../Types';
import { emptyFragment } from '@execonline-inc/execonline-ui';
import { memo, useEffect } from 'react';
import PlusMinusButton from '../../../AddParticipant/PlusMinusButton';
import Label from '../../../../../Form/Label';
import { AddLicenseToParticipantsStore } from '../Store';
import LicenseDropdown from './LicenseDropdown';
import { findField } from '../../../../../../Resource/FormTemplates/Types';
import { T } from '../../../../../../Translations';

interface Props {
  state: InitialReady | FetchingLicenseOptions | LicensesReady;
  store: AddLicenseToParticipantsStore;
}

function ProductLicense({ state, store }: Props) {
  useEffect(() => {
    if (state.kind === 'licenses-ready') {
      store.initializeLicenses();
    }
  }, [state.kind, store]);

  useEffect(() => {
    if (state.kind === 'licenses-ready') {
      store.syncLicensesToTemplate();
    }
  }, [state, store]);

  if (state.kind !== 'licenses-ready') {
    return emptyFragment();
  }

  const availableLicenses = state.resource.payload.allocatableOrReleasableLicenses;
  const licenseAction = findField('license_action', 'single-selection', state.template.fields)
    .andThen(({ value }) => fromNullable(value))
    .map((option) => option.value)
    .getOrElse(() => 'allocate');

  const actionText = licenseAction === 'release' ? 'Release' : 'Allocate';

  return fromEmpty(availableLicenses)
    .map(() => (
      <div className="mt-4">
        <Label labelText="License(s) to Manage" className="text-sm font-semibold" />
        <>
          {state.selectedLicenses.map((selectedLicense, index) => {
            const isLastLicense = index === state.selectedLicenses.length - 1;
            return (
              <LicenseDropdown
                key={index}
                selectedLicense={selectedLicense}
                index={index}
                isLastLicense={isLastLicense}
                store={store}
                state={state}
              />
            );
          })}
          {state.selectedLicenses.length === 0 && store.hasAvailableLicensesToAdd() && (
            <PlusMinusButton
              onClick={() => {
                store.addLicense();
              }}
              which="plus"
              data-intercom-target="bulk-license-assignment-add-first-license-button"
            />
          )}
        </>
      </div>
    ))
    .getOrElse(() => (
      <div className="mt-4 p-1 rounded-md text-sm">
        <T kind="No License to {{licenseAction}}" licenseAction={actionText} />
      </div>
    ));
}

export default memo(observer(ProductLicense));
